import React, { FC, useRef, useState } from "react"
import {
  useFloating,
  autoUpdate,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  offset,
  flip,
  arrow,
  FloatingArrow
} from "@floating-ui/react"

export type Alignment = "start" | "end"
export type Side = "top" | "right" | "bottom" | "left"
export type AlignedPlacement = `${Side}-${Alignment}`

export interface Props {
  toolTipNode: React.ReactElement
  text: React.ReactNode
  displayTextClass?: string
  placement?: Side | AlignedPlacement
  open?: boolean
}

const Tooltip: FC<Props> = ({
  toolTipNode,
  text,
  placement,
  displayTextClass = "",
  open: controlledOpen
}) => {
  const [internalOpen, setIsOpen] = useState(false)
  const isOpen = controlledOpen ?? internalOpen
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: placement ?? "top",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackAxisSideDirection: "start"
      }),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: "tooltip" })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ])

  return (
    <>
      <div
        className={`truncate ${displayTextClass}`}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {toolTipNode}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className="z-50 max-w-[10rem] md:max-w-xs bg-[#42405A] rounded-md text-neutral-50 text-xs md:text-sm p-2 break-all break-words"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <FloatingArrow ref={arrowRef} context={context} fill="#42405A" />
            {text}
          </div>
        )}
      </FloatingPortal>
    </>
  )
}

export default Tooltip
